import { useQueries } from 'react-query';
import { Metadata } from './types';
import MetadataService from '../../Api/meta-data-service';

export const baseKeys = [
  'ns_master_details',
  'ns_master_neckline',
  'ns_master_sleeveLength',
  'ns_master_length',
  'ns_master_fits',
  'ns_master_fits2',
  'ns_master_length',
  'ns_master_patterns',
  'ns_master_wash',
  'ns_master_heelHeight',
  'ns_master_heelType',
  'ns_master_shoeStyle',
  'ns_master_fabrics',
] as const;

const specialKeys = [
  'ns_master_merchHierarchy',
  'ns_master_fieldListOptionsPerMerchCategory',
  'attributes_required',
  'ns_master_vendorsMerchType',
] as const;

const validKeys = [...baseKeys, ...specialKeys];

/**
 * Metadata structure for categories
 *
 * @example
 * {
 *   "ns_master_merchHierarchy": {
 *     "categories": {
 *       "divisions": {
 *         "division1": {
 *           "department1": {
 *             "category1": {
 *               "class1": ["option1", "option2"],
 *               "class2": ["option3", "option4"]
 *             }
 *           }
 *         }
 *       }
 *     }
 *   }
 * }
 *
 * */
type CategoriesMetaDataValue = {
  /** Contains all divisions */
  divisions: {
    /** Each Division */
    [key: string]: {
      /** Each Department per Division */
      [key: string]: {
        /** Each Category per Department */
        [key: string]: {
          /** Each Class per Category */
          [key: string]: string[];
        };
      };
    };
  };
};

type VendorsMerchTypeMetadataValue = {
  vendorId: string;
  merchType: string;
  vendorName: string;
  domesticMerchPoEligible: boolean;
}[];

interface FieldsOptionsPerCategory {
  [key: string]: {
    [key: string]: string[];
  };
}

interface ItemFormFieldsDefinition {
  [key: string]: {
    [key: string]: string;
  };
}

type FormattedMetaData<T extends (typeof validKeys)[number]> = {
  [K in T]: K extends 'ns_master_merchHierarchy'
    ? CategoriesMetaDataValue
    : K extends 'ns_master_fieldListOptionsPerMerchCategory'
      ? FieldsOptionsPerCategory
      : K extends 'attributes_required'
        ? ItemFormFieldsDefinition
        : K extends 'ns_master_vendorsMerchType'
          ? VendorsMerchTypeMetadataValue
          : string[];
};

export function useGetMetaDatas<
  T extends readonly (typeof validKeys)[number][],
>(keys: T) {
  const results = useQueries(
    keys.map((key) => ({
      queryKey: ['metadata-service-meta-data', key],
      queryFn: async () => {
        const metaDatas = (await MetadataService.getMetaDatas([
          key,
        ])) as Metadata[];
        return metaDatas[0]?.value;
      },
      staleTime: 1000 * 60 * 60,
    })),
  );

  const data = keys.reduce((acc, key, idx) => {
    acc[key as string] = results[idx].data;
    return acc;
  }, {} as any) as FormattedMetaData<T[number]>;

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);

  return { data, isLoading, isError };
}
